import React, { useState } from 'react';
import {
  TextField, Button, Checkbox, FormControlLabel, Typography, Box, Slider, Snackbar, Alert,
  Card, CardContent, LinearProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import zxcvbn from 'zxcvbn';
import { useTheme } from '@mui/material/styles';


const PasswordGenerator = () => {
  const theme = useTheme();
  const [password, setPassword] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [length, setLength] = useState(4);
  const [uppercase, setUppercase] = useState(false);
  const [numbers, setNumbers] = useState(false);
  const [specials, setSpecials] = useState(false);

  const generatePassword = async () => {
    const params = new URLSearchParams({ length, uppercase, numbers, specials }).toString();
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${apiUrl}/api/generate/?${params}`);
    const data = await res.json();
    setPassword(data.password || '');
  };

  const copyToClipboard = () => {
    if (password) {
      navigator.clipboard.writeText(password)
        .then(() => {
          setSnackbarMessage('Password copied to clipboard!');
          setOpenSnackbar(true);
        })
        .catch(() => {
          setSnackbarMessage('Failed to copy password.');
          setOpenSnackbar(true);
        });
    } else {
      setSnackbarMessage('No password to copy!');
      setOpenSnackbar(true);
    }
  };

  const strength = zxcvbn(password).score;

  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['#d32f2f', '#f57c00', '#fbc02d', '#388e3c', '#2e7d32'];

  const strengthMessages = [
    "Very Weak: This password is easily guessable. Try adding some random words, maybe a cat's name?",
    "Weak: It's like a password written on a sticky note on your monitor.",
    "Fair: Getting there, but still kind of like your first password attempt in middle school.",
    "Good: Strong enough for most situations, but hackers with time on their hands might still get through.",
    "Strong: You’ve made a password that even your future self can’t crack without a hint. Excellent work! Maybe you used something like 'correct horse battery staple'."
  ];

  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Slider
        value={length}
        onChange={(e, val) => setLength(val)}
        min={1}
        max={10}
        step={1}
        valueLabelDisplay="auto"
        marks
      />
      <Typography>Password Length: {length} words</Typography>

      <FormControlLabel
        control={<Checkbox checked={uppercase} onChange={e => setUppercase(e.target.checked)} />}
        label="Include Uppercase"
      />
      <FormControlLabel
        control={<Checkbox checked={numbers} onChange={e => setNumbers(e.target.checked)} />}
        label="Include Numbers"
      />
      <FormControlLabel
        control={<Checkbox checked={specials} onChange={e => setSpecials(e.target.checked)} />}
        label="Include Special Characters"
      />

      <Button variant="contained" onClick={generatePassword}>Generate</Button>
      <br></br>

      <TextField
        label="Generated Password"
        value={password}
        InputProps={{
          readOnly: true,
          endAdornment: <Button onClick={copyToClipboard}><ContentCopyIcon /></Button>
        }}
        fullWidth
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: 2 }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: '100%', borderRadius: 2, fontWeight: 'bold', boxShadow: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {password && (
        <>
          {/* Strength Meter */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Password Strength: {strengthLabels[strength]}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(strength + 1) * 20}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: strengthColors[strength],
                }
              }}
            />
          </Box>

          {/* Humor Section */}
          <Card sx={{
            mt: 4,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f9f9f9',
            color: theme.palette.text.primary,
          }}>
            <CardContent>
              <Typography variant="h6">Fun Fact 💡</Typography>
              <Typography variant="body2">
                {strengthMessages[strength]}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    mt: 1,
                    fontStyle: 'italic',
                    color: 'gray',
                    a: {
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                    textDecoration: 'underline',
                    '&:visited': {
                        color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                    },
                    }
                }}
                >
                Inspired by <a href="https://xkcd.com/936/" target="_blank" rel="noopener noreferrer">XKCD #936 </a>
                </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PasswordGenerator;